// Tiny reCAPTCHA v3 helper — lazy-loads the Google script once and exposes a Promise-based
// execute(action) that returns the token. We use v3 (invisible, score-based) on the public
// /apply pages so we can gate spam without making the applicant solve a checkbox / image puzzle.
//
// Loaded only when the public surface needs it; never on authenticated pages so we don't pay the
// script cost (or send the user's session into Google's referer chain) where it isn't required.
//
// Returns null when the site key isn't configured (dev / preview environments) so the calling
// code can degrade gracefully — backend has a corresponding "captcha disabled" env flag for
// matching dev bypass.
//
// Phase 14ah — the SAME bundled SPA build is deployed to multiple hostnames (aumhum.com,
// aumhum.xyz, health.aumhum.xyz, localhost), each with a separately-registered Google reCAPTCHA
// site-key (different allowed-domain lists in Google admin). A static build-time
// VITE_GOOGLE_RECAPTCHA_SITE_KEY only works on one of those hosts — the others would fail with
// "Invalid domain for site key". We resolve the right key at RUNTIME from window.location.hostname
// using the host→key map below; the env var stays as the final fallback so dev/preview loads
// keep working.

const RECAPTCHA_SCRIPT_ID = 'recaptcha-v3-script'

// Host → reCAPTCHA v3 site-key. Mirrors the backend's recaptcha.credentials.* blocks in
// application.properties — adding a new host means one entry here AND one credential block on
// the server. Keys are normalised to lowercase. The values are PUBLIC site-keys, not secrets.
const HOSTNAME_TO_SITE_KEY = {
  'aumhum.com':         '6LdcuxclAAAAACZe5qSGEu5SY85yR6tIHavDQ4Ti',
  'www.aumhum.com':     '6LdcuxclAAAAACZe5qSGEu5SY85yR6tIHavDQ4Ti',
  'aumhum.xyz':         '6LcVYsksAAAAABuSM4XFk54KCfsqXOAvCIvxafTz',
  'www.aumhum.xyz':     '6LcVYsksAAAAABuSM4XFk54KCfsqXOAvCIvxafTz',
  'health.aumhum.xyz':  '6LckjcgsAAAAAOfvz7qiM8gh7xEysH9ZzsX9x7nx',
  'manage.aumhum.com':  '6Lcg9gEsAAAAALQznhlY17lCBJ6NYYeg0AWYpWY3',
  'localhost':          '6Lcg9gEsAAAAALQznhlY17lCBJ6NYYeg0AWYpWY3',
  '127.0.0.1':          '6Lcg9gEsAAAAALQznhlY17lCBJ6NYYeg0AWYpWY3',
}

function resolveSiteKey() {
  if (typeof window === 'undefined') return ''
  const host = (window.location?.hostname || '').toLowerCase()
  if (host && HOSTNAME_TO_SITE_KEY[host]) return HOSTNAME_TO_SITE_KEY[host]
  // Phase 14am — used to fall back to import.meta?.env?.VITE_GOOGLE_RECAPTCHA_SITE_KEY for
  // dev/preview/CI envs that aren't in HOSTNAME_TO_SITE_KEY, but Jest's babel transform
  // doesn't handle import.meta and the prebuild test guard tripped on it. localhost +
  // 127.0.0.1 are already in the map, which covers every dev/CI host we run today; if a
  // new host needs handling, add it to HOSTNAME_TO_SITE_KEY (the same source-of-truth the
  // backend's recaptcha.credentials.* config mirrors). Empty return makes loadScript a
  // no-op on unrecognised hosts, which matches the previous behaviour.
  return ''
}

let scriptPromise = null
let cachedSiteKey = null
function getSiteKey() {
  if (cachedSiteKey === null) cachedSiteKey = resolveSiteKey()
  return cachedSiteKey
}

function loadScript() {
  if (typeof window === 'undefined') return Promise.resolve(null)
  const siteKey = getSiteKey()
  if (!siteKey) return Promise.resolve(null)
  if (scriptPromise) return scriptPromise
  if (window.grecaptcha && window.grecaptcha.execute) {
    scriptPromise = Promise.resolve(window.grecaptcha)
    return scriptPromise
  }
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.grecaptcha))
      existing.addEventListener('error', () => reject(new Error('recaptcha-v3 load failed')))
      return
    }
    const script = document.createElement('script')
    script.id = RECAPTCHA_SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.defer = true
    script.onload = () => {
      // grecaptcha.ready guarantees the global is fully initialised before we call execute.
      window.grecaptcha?.ready(() => resolve(window.grecaptcha))
    }
    script.onerror = () => reject(new Error('recaptcha-v3 load failed'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

/**
 * Run a v3 challenge for `action` and return the token string. Returns null when reCAPTCHA isn't
 * configured (dev) — caller should pass null straight through; the backend's captcha-disabled
 * flag will accept the missing token in matching env modes.
 */
export async function executeRecaptchaV3(action) {
  try {
    const grecaptcha = await loadScript()
    const siteKey = getSiteKey()
    if (!grecaptcha || !siteKey) return null
    return await grecaptcha.execute(siteKey, { action })
  } catch {
    return null
  }
}

export function isRecaptchaConfigured() {
  return Boolean(getSiteKey())
}

/**
 * Phase 14ah — exposed so consumers that need the site-key directly (e.g. the v2-style
 * checkbox in helperComponents.jsx) get the same hostname-aware resolution.
 */
export function getRecaptchaSiteKey() {
  return getSiteKey()
}
